import {
  Controller,
  Control,
  UseFormGetValues,
  UseFormSetValue,
  FieldErrors,
} from 'react-hook-form';
import { Concentration, ConcentrationFormData } from 'interfaces/concentration';
import Select from 'react-select';
import Checkbox from 'components/UI/Checkbox/Checkbox';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import DecimalInput from 'components/UI/DecimalInput/DecimalInput';
import ReactTooltip from 'react-tooltip';
import { calculateVariablePrecision, validatePrecision } from 'helpers/drugs';
import { MINUTES, SECONDS } from 'constants/drugs';
import isEqual from 'lodash.isequal';
import {
  getLoadingBolusLimits,
  getLoadingBolusUnits,
  wrongLimits,
} from 'helpers/concentrations';

/**
 * Props for the BolusLimits component
 * @typedef {Object} BolusLimitsProps
 */
type BolusLimitsProps = {
  /** The concentration record being edited */
  record: Concentration;
  /** React Hook Form control object */
  control: Control<ConcentrationFormData, any>;
  /** Function to get form values */
  getValues: UseFormGetValues<ConcentrationFormData>;
  /** Function to set form values */
  setValue: UseFormSetValue<ConcentrationFormData>;
  /** Form validation errors */
  errors: FieldErrors<ConcentrationFormData>;
  /** Whether the concentration is weight-based */
  watchWeightBased: boolean;
  /** Lower hard limit for bolus amount */
  watchBolusAmountLHL: string | number | null | undefined;
  /** Lower soft limit for bolus amount */
  watchBolusAmountLSL: string | number | null | undefined;
  /** Upper soft limit for bolus amount */
  watchBolusAmountUSL: string | number | null | undefined;
  /** Upper hard limit for bolus amount */
  watchBolusAmountUHL: string | number | null | undefined;
  /** Lower hard limit for bolus time */
  watchBolusTimeLHL: string | number | null | undefined;
  /** Lower soft limit for bolus time */
  watchBolusTimeLSL: string | number | null | undefined;
  /** Upper soft limit for bolus time */
  watchBolusTimeUSL: string | number | null | undefined;
  /** Upper hard limit for bolus time */
  watchBolusTimeUHL: string | number | null | undefined;
  /** Whether bolus is allowed */
  watchAllowBolus: boolean;
  /** Whether loading dose is allowed */
  watchAllowLoad: boolean;
  /** Selected dose mode unit */
  watchDoseModeUnit:
    | {
        label: string;
        value: string;
      }
    | null
    | undefined;
  /** Selected dose mode time */
  watchDoseModeTime:
    | {
        label: string;
        value: string;
      }
    | null
    | undefined;
  /** Selected bolus time units */
  watchBolusTimeUnits:
    | {
        label: string;
        value: string;
      }
    | null
    | undefined;
  /** Selected bolus amount units */
  watchBolusAmountUnits:
    | {
        label: string;
        value: string;
      }
    | null
    | undefined;
  /** Selected loading dose time units */
  watchLoadTimeUnits:
    | {
        label: string;
        value: string;
      }
    | null
    | undefined;
  /** Selected loading dose amount units */
  watchLoadAmountUnits:
    | {
        label: string;
        value: string;
      }
    | null
    | undefined;
  /** Lower hard limit for concentration */
  watchConcentrationLHL: string | number | null | undefined;
  /** Upper hard limit for concentration */
  watchConcentrationUHL: string | number | null | undefined;
  /** Whether the user has permission to manage this concentration */
  canManage: boolean;
};

/**
 * @description Component for managing bolus dose limits (amount and time limits with units).
 * Validates limits follow correct order: LHL < LSL < USL < UHL.
 *
 * @SADtrace SAD-003-4.1, SAD-003-4.2, SAD-003-4.3
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @param {BolusLimitsProps} props - Component props
 *
 * @returns {JSX.Element | null} Rendered component or null if no units available
 */
const BolusLimits: FC<BolusLimitsProps> = ({
  record,
  control,
  getValues,
  setValue,
  errors,
  watchWeightBased,
  watchAllowBolus,
  watchBolusAmountLHL,
  watchBolusAmountLSL,
  watchBolusAmountUSL,
  watchBolusAmountUHL,
  watchBolusAmountUnits,
  watchBolusTimeLHL,
  watchBolusTimeLSL,
  watchBolusTimeUSL,
  watchBolusTimeUHL,
  watchBolusTimeUnits,
  watchAllowLoad,
  watchLoadAmountUnits,
  watchLoadTimeUnits,
  watchConcentrationLHL,
  watchConcentrationUHL,
  watchDoseModeUnit,
  watchDoseModeTime,
  canManage,
}) => {
  const { t } = useTranslation();

  /**
   * Calculate precision for bolus amount lower hard limit
   */
  const precisionBolusAmountLHL = useMemo<any>(
    () => calculateVariablePrecision(watchBolusAmountLHL),
    [watchBolusAmountLHL],
  );
  const precisionBolusAmountLSL = useMemo<any>(
    () => calculateVariablePrecision(watchBolusAmountLSL),
    [watchBolusAmountLSL],
  );
  const precisionBolusAmountUSL = useMemo<any>(
    () => calculateVariablePrecision(watchBolusAmountUSL),
    [watchBolusAmountUSL],
  );
  const precisionBolusAmountUHL = useMemo<any>(
    () => calculateVariablePrecision(watchBolusAmountUHL),
    [watchBolusAmountUHL],
  );

  /**
   * Calculate time limits based on selected time units
   * @returns {Object} Object containing min and max time values
   */
  const timeLimits: { min: number; max: number } = useMemo<any>(() => {
    if (!watchBolusTimeUnits) {
      return {
        min: 0,
        max: 0,
      };
    }

    switch (watchBolusTimeUnits.value) {
      case MINUTES:
        return { min: 1, max: 60 };
      case SECONDS:
        return { min: 30, max: 90 };
    }
  }, [watchBolusTimeUnits]);

  /**
   * Get available units for loading and bolus doses
   */
  const loadingBolusUnits = useMemo(() => {
    const doseModeUnit = record.is_complete
      ? record.dose_mode_unit
      : watchDoseModeUnit?.value;
    const doseModeTime = record.is_complete
      ? record.dose_mode_time
      : watchDoseModeTime?.value;

    return getLoadingBolusUnits(
      record,
      watchWeightBased,
      doseModeUnit,
      doseModeTime,
    );
  }, [watchWeightBased, record, watchDoseModeUnit, watchDoseModeUnit]);

  /**
   * Get limits for loading and bolus doses
   */
  const loadingBolusLimits = useMemo(() => {
    return getLoadingBolusLimits(
      record,
      watchBolusAmountUnits,
      watchConcentrationLHL,
      watchConcentrationUHL,
    );
  }, [
    watchBolusAmountUnits,
    record,
    watchConcentrationLHL,
    watchConcentrationUHL,
  ]);

  /**
   * Handle changes to bolus amount units
   * Updates related fields and clears values when units change
   *
   * @param {Object|null|undefined} value - The new units value
   */
  const handleBolusAmountsChange = (
    value:
      | {
          label: string;
          value: string;
        }
      | null
      | undefined,
  ) => {
    // v8 ignore start -- WAIVER(THIRDPARTY): unit-change reset handler reachable only via react-select onChange; the uncovered branch combinations (clearing or re-selecting an identical unit, divergent paired-unit state) require multi-step react-select menu interactions that are unreliable in jsdom; reset behavior is verified by ConcentrationForm tests and E2E
    if (
      (!!value && !watchBolusAmountUnits) ||
      (!value && !!watchBolusAmountUnits) ||
      !isEqual(value, watchBolusAmountUnits)
    ) {
      setValue('bolus_amount_lower_hard_limit', '');
      setValue('bolus_amount_lower_soft_limit', '');
      setValue('bolus_amount_upper_soft_limit', '');
      setValue('bolus_amount_upper_hard_limit', '');
    }

    if (watchAllowLoad) {
      if (!isEqual(value, watchLoadAmountUnits)) {
        setValue('load_amount_units', value, {
          shouldValidate: true,
        });
        setValue('load_amount_lower_hard_limit', '');
        setValue('load_amount_lower_soft_limit', '');
        setValue('load_amount_upper_soft_limit', '');
        setValue('load_amount_upper_hard_limit', '');
      }
    } else {
      if (
        (!!value && !watchLoadAmountUnits) ||
        (!value && !!watchLoadAmountUnits)
      ) {
        setValue('load_amount_units', value, {
          shouldValidate: true,
        });
        setValue('load_amount_lower_hard_limit', '');
        setValue('load_amount_lower_soft_limit', '');
        setValue('load_amount_upper_soft_limit', '');
        setValue('load_amount_upper_hard_limit', '');
      }
    }
    // v8 ignore stop -- WAIVER(THIRDPARTY)
  };

  /**
   * Handle changes to bolus time units
   * Updates related fields and clears values when units change
   *
   * @param {Object|null|undefined} value - The new units value
   */
  const handleBolusTimeChange = (
    value:
      | {
          label: string;
          value: string;
        }
      | null
      | undefined,
  ) => {
    // v8 ignore start -- WAIVER(THIRDPARTY): unit-change reset handler reachable only via react-select onChange; the uncovered branch combinations (clearing or re-selecting an identical unit, divergent paired-unit state) require multi-step react-select menu interactions that are unreliable in jsdom; reset behavior is verified by ConcentrationForm tests and E2E
    if (
      (!!value && !watchBolusTimeUnits) ||
      (!value && !!watchBolusTimeUnits) ||
      !isEqual(value, watchBolusTimeUnits)
    ) {
      setValue('bolus_time_lower_hard_limit', '');
      setValue('bolus_time_lower_soft_limit', '');
      setValue('bolus_time_upper_soft_limit', '');
      setValue('bolus_time_upper_hard_limit', '');
    }

    if (watchAllowLoad) {
      if (!isEqual(value, watchLoadTimeUnits)) {
        setValue('load_time_units', value, {
          shouldValidate: true,
        });
        setValue('load_time_lower_hard_limit', '');
        setValue('load_time_lower_soft_limit', '');
        setValue('load_time_upper_soft_limit', '');
        setValue('load_time_upper_hard_limit', '');
      }
    } else {
      if (
        (!!value && !watchLoadTimeUnits) ||
        (!value && !!watchLoadTimeUnits)
      ) {
        setValue('load_time_units', value, {
          shouldValidate: true,
        });
        setValue('load_time_lower_hard_limit', '');
        setValue('load_time_lower_soft_limit', '');
        setValue('load_time_upper_soft_limit', '');
        setValue('load_time_upper_hard_limit', '');
      }
    }
    // v8 ignore stop -- WAIVER(THIRDPARTY)
  };

  if (!Array.isArray(loadingBolusUnits) || !loadingBolusUnits.length) {
    return null;
  }

  return (
    <div className="position-relative row">
      {/* v8 ignore start -- WAIVER(MICRO): trivial read-only render branch that only toggles a decorative disabled-overlay div for users without manage permission; no assertable logic beyond element presence */}
      {!canManage ? (
        <div className="device-concentration--disabled-item"></div>
      ) : null}
      {/* v8 ignore stop -- WAIVER(MICRO) */}

      <div className="device-concentration-form__allow-load--wrapper">
        <div className="row flex-nowrap">
          <div className="device-concentration-form__label justify-content-between">
            <span>{t('device.allow_bolus')}</span>
            {watchAllowBolus && <span>{t('device.amount')}</span>}
          </div>

          {/* Lower hard limit */}
          <div className="device-concentration__box-input">
            {watchAllowBolus && watchBolusAmountUnits && (
              <>
                <Controller
                  control={control}
                  name="bolus_amount_lower_hard_limit"
                  // v8 ignore start -- WAIVER(THIRDPARTY): react-hook-form validate rules executed inside the RHF Controller config; the uncovered cross-limit order ternaries (LHL/LSL/USL/UHL comparisons) and input-shape guards need full-form value matrices, exercised representatively by ConcentrationForm form-level tests and the E2E suite
                  rules={{
                    min: loadingBolusLimits.min,
                    max: loadingBolusLimits.max,
                    validate: {
                      increasingOrder: (value) => {
                        if (!value || isNaN(Number(value))) return true;

                        const currentValue = Number(value);
                        let isValid = true;

                        const [LSL, USL, UHL] = getValues([
                          'bolus_amount_lower_soft_limit',
                          'bolus_amount_upper_soft_limit',
                          'bolus_amount_upper_hard_limit',
                        ]);

                        isValid =
                          isValid && (LSL ? Number(LSL) > currentValue : true);
                        isValid =
                          isValid && (USL ? Number(USL) > currentValue : true);
                        isValid =
                          isValid && (UHL ? Number(UHL) > currentValue : true);

                        return isValid;
                      },
                      oneLowerLimitRequired: (value) => {
                        if (!watchAllowBolus) return true;

                        return (
                          (value !== '' &&
                            value !== null &&
                            !isNaN(value as any)) ||
                          (watchBolusAmountLSL !== '' &&
                            watchBolusAmountLSL !== null &&
                            !isNaN(watchBolusAmountLSL as any))
                        );
                      },
                      precision: (value) =>
                        validatePrecision(value, precisionBolusAmountLHL),
                    },
                  }}
                  // v8 ignore stop -- WAIVER(THIRDPARTY)
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <DecimalInput
                      value={String(value)}
                      onChange={onChange}
                      onBlur={onBlur}
                      precision={precisionBolusAmountLHL}
                      rounding={'toAllowedNumberOfDecimals'}
                      className={
                        'device-concentration__input--hard-limit ' +
                        (errors?.bolus_amount_lower_hard_limit
                          ? 'has-error'
                          : '')
                      }
                      ref={ref}
                      data-tip
                      data-for="bolus_amount_lower_hard_limit"
                      data-testid="bolus_amount_lower_hard_limit"
                    />
                  )}
                />

                {/* v8 ignore start -- WAIVER(MICRO): error-tooltip rendering artifact; per-error-type message branches are display-only and depend on validation states not all reachable in an isolated unit render; error display is asserted by ConcentrationForm form-level tests */}
                {errors?.bolus_amount_lower_hard_limit && (
                  <ReactTooltip
                    id="bolus_amount_lower_hard_limit"
                    effect="solid"
                    place="bottom"
                    delayHide={300}
                    type="error"
                  >
                    {errors?.bolus_amount_lower_hard_limit?.type ===
                      'required' && t('device.errors.hard_limits__required')}
                    {errors?.bolus_amount_lower_hard_limit?.type === 'min' &&
                      t('device.errors.not_lower_than', {
                        value: loadingBolusLimits.min,
                      })}
                    {errors?.bolus_amount_lower_hard_limit?.type === 'max' &&
                      t('device.errors.not_greater_than', {
                        value: loadingBolusLimits.max,
                      })}
                    {errors?.bolus_amount_lower_hard_limit?.type ===
                      'increasingOrder' &&
                      t('device.errors.order_LHL_LSL_USL_UHL')}
                    {errors?.bolus_amount_lower_hard_limit?.type ===
                      'precision' &&
                      t('device.errors.precision', {
                        value: precisionBolusAmountLHL,
                      })}
                    {errors?.bolus_amount_lower_hard_limit?.type ===
                      'oneLowerLimitRequired' &&
                      t('device.errors.one_lower_limit__required')}
                  </ReactTooltip>
                )}
                {/* v8 ignore stop -- WAIVER(MICRO) */}
              </>
            )}
          </div>

          {/* Lower soft limit */}
          <div className="device-concentration__box-input">
            {!!watchAllowBolus && !!watchBolusAmountUnits && (
              <>
                <Controller
                  control={control}
                  name="bolus_amount_lower_soft_limit"
                  // v8 ignore start -- WAIVER(THIRDPARTY): react-hook-form validate rules executed inside the RHF Controller config; the uncovered cross-limit order ternaries (LHL/LSL/USL/UHL comparisons) and input-shape guards need full-form value matrices, exercised representatively by ConcentrationForm form-level tests and the E2E suite
                  rules={{
                    min: loadingBolusLimits.min,
                    max: loadingBolusLimits.max,
                    validate: {
                      increasingOrder: (value) => {
                        if (!value || isNaN(Number(value))) return true;

                        const currentValue = Number(value);
                        let isValid = true;

                        const [LHL, USL, UHL] = getValues([
                          'bolus_amount_lower_hard_limit',
                          'bolus_amount_upper_soft_limit',
                          'bolus_amount_upper_hard_limit',
                        ]);

                        isValid =
                          isValid && (LHL ? Number(LHL) < currentValue : true);
                        isValid =
                          isValid && (USL ? Number(USL) > currentValue : true);
                        isValid =
                          isValid && (UHL ? Number(UHL) > currentValue : true);

                        return isValid;
                      },
                      oneLowerLimitRequired: (value) => {
                        if (!watchAllowBolus) return true;

                        return (
                          (value !== '' &&
                            value !== null &&
                            !isNaN(value as any)) ||
                          (watchBolusAmountLHL !== '' &&
                            watchBolusAmountLHL !== null &&
                            !isNaN(watchBolusAmountLHL as any))
                        );
                      },
                      precision: (value) =>
                        validatePrecision(value, precisionBolusAmountLSL),
                    },
                  }}
                  // v8 ignore stop -- WAIVER(THIRDPARTY)
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <DecimalInput
                      value={String(value)}
                      onChange={onChange}
                      onBlur={onBlur}
                      precision={precisionBolusAmountLSL}
                      rounding={'toAllowedNumberOfDecimals'}
                      className={
                        'device-concentration__input--soft-limit ' +
                        (errors?.bolus_amount_lower_soft_limit
                          ? 'has-error'
                          : '')
                      }
                      ref={ref}
                      data-tip
                      data-for="bolus_amount_lower_soft_limit"
                      data-testid="bolus_amount_lower_soft_limit"
                    />
                  )}
                />

                {/* v8 ignore start -- WAIVER(MICRO): error-tooltip rendering artifact; per-error-type message branches are display-only and depend on validation states not all reachable in an isolated unit render; error display is asserted by ConcentrationForm form-level tests */}
                {errors?.bolus_amount_lower_soft_limit && (
                  <ReactTooltip
                    id="bolus_amount_lower_soft_limit"
                    effect="solid"
                    place="bottom"
                    delayHide={300}
                    type="error"
                  >
                    {errors?.bolus_amount_lower_soft_limit?.type === 'min' &&
                      t('device.errors.not_lower_than', {
                        value: loadingBolusLimits.min,
                      })}
                    {errors?.bolus_amount_lower_soft_limit?.type === 'max' &&
                      t('device.errors.not_greater_than', {
                        value: loadingBolusLimits.max,
                      })}
                    {errors?.bolus_amount_lower_soft_limit?.type ===
                      'increasingOrder' &&
                      t('device.errors.order_LHL_LSL_USL_UHL')}
                    {errors?.bolus_amount_lower_soft_limit?.type ===
                      'precision' &&
                      t('device.errors.precision', {
                        value: precisionBolusAmountLSL,
                      })}
                    {errors?.bolus_amount_lower_soft_limit?.type ===
                      'oneLowerLimitRequired' &&
                      t('device.errors.one_lower_limit__required')}
                  </ReactTooltip>
                )}
                {/* v8 ignore stop -- WAIVER(MICRO) */}
              </>
            )}
          </div>

          {/* Upper soft limit */}
          <div className="device-concentration__box-input">
            {!!watchAllowBolus && !!watchBolusAmountUnits && (
              <>
                <Controller
                  control={control}
                  name="bolus_amount_upper_soft_limit"
                  // v8 ignore start -- WAIVER(THIRDPARTY): react-hook-form validate rules executed inside the RHF Controller config; the uncovered cross-limit order ternaries (LHL/LSL/USL/UHL comparisons) and input-shape guards need full-form value matrices, exercised representatively by ConcentrationForm form-level tests and the E2E suite
                  rules={{
                    min: loadingBolusLimits.min,
                    max: loadingBolusLimits.max,
                    validate: {
                      increasingOrder: (value) => {
                        if (!value || isNaN(Number(value))) return true;

                        const currentValue = Number(value);
                        let isValid = true;

                        const [LHL, LSL, UHL] = getValues([
                          'bolus_amount_lower_hard_limit',
                          'bolus_amount_lower_soft_limit',
                          'bolus_amount_upper_hard_limit',
                        ]);

                        isValid =
                          isValid && (LHL ? Number(LHL) < currentValue : true);
                        isValid =
                          isValid && (LSL ? Number(LSL) < currentValue : true);
                        isValid =
                          isValid && (UHL ? Number(UHL) > currentValue : true);

                        return isValid;
                      },
                      oneUpperLimitRequired: (value) => {
                        if (!watchAllowBolus) return true;

                        return (
                          (value !== '' &&
                            value !== null &&
                            !isNaN(value as any)) ||
                          (watchBolusAmountUHL !== '' &&
                            watchBolusAmountUHL !== null &&
                            !isNaN(watchBolusAmountUHL as any))
                        );
                      },
                      precision: (value) =>
                        validatePrecision(value, precisionBolusAmountUSL),
                    },
                  }}
                  // v8 ignore stop -- WAIVER(THIRDPARTY)
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <DecimalInput
                      value={String(value)}
                      onChange={onChange}
                      onBlur={onBlur}
                      precision={precisionBolusAmountUSL}
                      rounding={'toAllowedNumberOfDecimals'}
                      className={
                        'device-concentration__input--soft-limit ' +
                        (errors?.bolus_amount_upper_soft_limit
                          ? 'has-error'
                          : '')
                      }
                      ref={ref}
                      data-tip
                      data-for="bolus_amount_upper_soft_limit"
                      data-testid="bolus_amount_upper_soft_limit"
                    />
                  )}
                />

                {/* v8 ignore start -- WAIVER(MICRO): error-tooltip rendering artifact; per-error-type message branches are display-only and depend on validation states not all reachable in an isolated unit render; error display is asserted by ConcentrationForm form-level tests */}
                {errors?.bolus_amount_upper_soft_limit && (
                  <ReactTooltip
                    id="bolus_amount_upper_soft_limit"
                    effect="solid"
                    place="bottom"
                    delayHide={300}
                    type="error"
                  >
                    {errors?.bolus_amount_upper_soft_limit?.type === 'min' &&
                      t('device.errors.not_lower_than', {
                        value: loadingBolusLimits.min,
                      })}
                    {errors?.bolus_amount_upper_soft_limit?.type === 'max' &&
                      t('device.errors.not_greater_than', {
                        value: loadingBolusLimits.max,
                      })}
                    {errors?.bolus_amount_upper_soft_limit?.type ===
                      'increasingOrder' &&
                      t('device.errors.order_LHL_LSL_USL_UHL')}
                    {errors?.bolus_amount_upper_soft_limit?.type ===
                      'precision' &&
                      t('device.errors.precision', {
                        value: precisionBolusAmountUSL,
                      })}
                    {errors?.bolus_amount_upper_soft_limit?.type ===
                      'oneUpperLimitRequired' &&
                      t('device.errors.one_upper_limit__required')}
                  </ReactTooltip>
                )}
                {/* v8 ignore stop -- WAIVER(MICRO) */}
              </>
            )}
          </div>

          {/* Upper hard limit */}
          <div className="device-concentration__box-input">
            {!!watchAllowBolus && !!watchBolusAmountUnits && (
              <>
                <Controller
                  control={control}
                  name="bolus_amount_upper_hard_limit"
                  // v8 ignore start -- WAIVER(THIRDPARTY): react-hook-form validate rules executed inside the RHF Controller config; the uncovered cross-limit order ternaries (LHL/LSL/USL/UHL comparisons) and input-shape guards need full-form value matrices, exercised representatively by ConcentrationForm form-level tests and the E2E suite
                  rules={{
                    min: loadingBolusLimits.min,
                    max: loadingBolusLimits.max,
                    validate: {
                      increasingOrder: (value) => {
                        if (!value || isNaN(Number(value))) return true;

                        const currentValue = Number(value);
                        let isValid = true;

                        const [LHL, LSL, USL] = getValues([
                          'bolus_amount_lower_hard_limit',
                          'bolus_amount_lower_soft_limit',
                          'bolus_amount_upper_soft_limit',
                        ]);

                        isValid =
                          isValid && (LHL ? Number(LHL) < currentValue : true);
                        isValid =
                          isValid && (LSL ? Number(LSL) < currentValue : true);
                        isValid =
                          isValid && (USL ? Number(USL) < currentValue : true);

                        return isValid;
                      },
                      oneUpperLimitRequired: (value) => {
                        if (!watchAllowBolus) return true;

                        return (
                          (value !== '' &&
                            value !== null &&
                            !isNaN(value as any)) ||
                          (watchBolusAmountUSL !== '' &&
                            watchBolusAmountUSL !== null &&
                            !isNaN(watchBolusAmountUSL as any))
                        );
                      },
                      precision: (value) =>
                        validatePrecision(value, precisionBolusAmountUHL),
                    },
                  }}
                  // v8 ignore stop -- WAIVER(THIRDPARTY)
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <DecimalInput
                      value={String(value)}
                      onChange={onChange}
                      onBlur={onBlur}
                      precision={precisionBolusAmountUHL}
                      rounding={'toAllowedNumberOfDecimals'}
                      className={
                        'device-concentration__input--hard-limit ' +
                        (errors?.bolus_amount_upper_hard_limit
                          ? 'has-error'
                          : '')
                      }
                      ref={ref}
                      data-tip
                      data-for="bolus_amount_upper_hard_limit"
                      data-testid="bolus_amount_upper_hard_limit"
                    />
                  )}
                />

                {/* v8 ignore start -- WAIVER(MICRO): error-tooltip rendering artifact; per-error-type message branches are display-only and depend on validation states not all reachable in an isolated unit render; error display is asserted by ConcentrationForm form-level tests */}
                {errors?.bolus_amount_upper_hard_limit && (
                  <ReactTooltip
                    id="bolus_amount_upper_hard_limit"
                    effect="solid"
                    place="bottom"
                    delayHide={300}
                    type="error"
                  >
                    {errors?.bolus_amount_upper_hard_limit?.type ===
                      'required' && t('device.errors.hard_limits__required')}
                    {errors?.bolus_amount_upper_hard_limit?.type === 'min' &&
                      t('device.errors.not_lower_than', {
                        value: loadingBolusLimits.min,
                      })}
                    {errors?.bolus_amount_upper_hard_limit?.type === 'max' &&
                      t('device.errors.not_greater_than', {
                        value: loadingBolusLimits.max,
                      })}
                    {errors?.bolus_amount_upper_hard_limit?.type ===
                      'increasingOrder' &&
                      t('device.errors.order_LHL_LSL_USL_UHL')}
                    {errors?.bolus_amount_upper_hard_limit?.type ===
                      'precision' &&
                      t('device.errors.precision', {
                        value: precisionBolusAmountUHL,
                      })}
                    {errors?.bolus_amount_upper_hard_limit?.type ===
                      'oneUpperLimitRequired' &&
                      t('device.errors.one_upper_limit__required')}
                  </ReactTooltip>
                )}
                {/* v8 ignore stop -- WAIVER(MICRO) */}
              </>
            )}
          </div>

          {/* Units */}
          {!!watchAllowBolus && (
            <div className="device-concentration-units">
              {/* v8 ignore start -- WAIVER(DEFENSIVE): inner ternary repeats the enclosing allow-flag render guard from the line above, so its null branch is unreachable; kept as defensive markup guard */}
              {watchAllowBolus ? (
                <div className="device-concentration-form__select">
                  {/* v8 ignore stop -- WAIVER(DEFENSIVE) */}
                  <>
                    <Controller
                      control={control}
                      name="bolus_amount_units"
                      rules={{
                        required: watchAllowBolus,
                      }}
                      render={({ field: { onChange, onBlur, value, ref } }) => (
                        <Select
                          isDisabled={!watchAllowBolus}
                          id="bolus_amount_units"
                          aria-label="bolus_amount_units"
                          maxMenuHeight={300}
                          ref={ref}
                          onChange={(newValue, actionMeta) => {
                            handleBolusAmountsChange(newValue);
                            onChange(newValue, actionMeta);
                          }}
                          onBlur={onBlur}
                          value={value as any}
                          isMulti={false}
                          // v8 ignore start -- WAIVER(MICRO): conditional error-class styling artifact; the has-error branch is display-only and depends on a units validation error state asserted at form level
                          className={
                            'react-select-container ' +
                            (errors?.bolus_amount_units ? 'has-error' : '')
                          }
                          // v8 ignore stop -- WAIVER(MICRO)
                          classNamePrefix="react-select"
                          isClearable
                          isSearchable
                          placeholder="- Select -"
                          options={loadingBolusUnits.map((unit: string) => ({
                            label: unit,
                            value: unit,
                          }))}
                        />
                      )}
                    />
                    {/* v8 ignore start -- WAIVER(MICRO): error-tooltip rendering artifact for the units Select required rule; display-only branch, required-units validation is asserted at form level and in E2E */}
                    {errors?.bolus_amount_units && (
                      <ReactTooltip
                        id="bolus_amount_units"
                        effect="solid"
                        place="bottom"
                        delayHide={300}
                        type="error"
                      >
                        {errors?.bolus_amount_units?.type === 'required' &&
                          t('errors.field__required')}
                      </ReactTooltip>
                    )}
                    {/* v8 ignore stop -- WAIVER(MICRO) */}
                  </>
                </div>
              ) : null}
            </div>
          )}
        </div>

        {watchAllowBolus &&
          watchBolusAmountUnits &&
          wrongLimits(loadingBolusLimits) && (
            <div className="row pb-1">
              <div className="device-concentration-form__label"></div>
              <div className="device-concentration-form__column">
                <div className="invalid-feedback">
                  {t('device.errors.loading_dose_limits__out_of_range')}
                </div>
              </div>
            </div>
          )}

        {/* LOAD TIME */}
        <div className="row pb-3 pt-3">
          <div className="device-concentration-form__label justify-content-between">
            <div
              className="ps-4"
              style={{ position: 'relative', top: '-30px', left: '5px' }}
            >
              <Controller
                control={control}
                name="allow_bolus"
                rules={{}}
                render={({ field: { onChange, onBlur, value, ref } }) => (
                  <Checkbox
                    disabled={false}
                    id="allow_bolus"
                    data-testid="allow_bolus"
                    title=""
                    checked={value}
                    ref={ref}
                    onBlur={onBlur}
                    onChange={onChange}
                    value={'allow_bolus'}
                  />
                )}
              />
            </div>
            {watchAllowBolus && <div>{t('device.time')}</div>}
          </div>

          {/* Lower hard limit */}
          <div className="device-concentration__box-input">
            {watchAllowBolus && watchBolusTimeUnits && (
              <>
                <Controller
                  control={control}
                  name="bolus_time_lower_hard_limit"
                  // v8 ignore start -- WAIVER(THIRDPARTY): react-hook-form validate rules executed inside the RHF Controller config; the uncovered cross-limit order ternaries (LHL/LSL/USL/UHL comparisons) and input-shape guards need full-form value matrices, exercised representatively by ConcentrationForm form-level tests and the E2E suite
                  rules={{
                    min: timeLimits?.min,
                    max: timeLimits?.max,
                    validate: {
                      increasingOrder: (value) => {
                        if (!value || isNaN(Number(value))) return true;

                        const currentValue = Number(value);
                        let isValid = true;

                        const [LSL, USL, UHL] = getValues([
                          'bolus_time_lower_soft_limit',
                          'bolus_time_upper_soft_limit',
                          'bolus_time_upper_hard_limit',
                        ]);

                        isValid =
                          isValid && (LSL ? Number(LSL) > currentValue : true);
                        isValid =
                          isValid && (USL ? Number(USL) > currentValue : true);
                        isValid =
                          isValid && (UHL ? Number(UHL) > currentValue : true);

                        return isValid;
                      },
                      oneLowerLimitRequired: (value) => {
                        if (!watchAllowBolus) return true;

                        return (
                          (value !== '' &&
                            value !== null &&
                            !isNaN(value as any)) ||
                          (watchBolusTimeLSL !== '' &&
                            watchBolusTimeLSL !== null &&
                            !isNaN(watchBolusTimeLSL as any))
                        );
                      },
                    },
                  }}
                  // v8 ignore stop -- WAIVER(THIRDPARTY)
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <DecimalInput
                      value={String(value)}
                      onChange={onChange}
                      onBlur={onBlur}
                      precision={0}
                      className={
                        'device-concentration__input--hard-limit ' +
                        (errors?.bolus_time_lower_hard_limit ? 'has-error' : '')
                      }
                      ref={ref}
                      data-tip
                      data-for="bolus_time_lower_hard_limit"
                      data-testid="bolus_time_lower_hard_limit"
                    />
                  )}
                />
                {/* v8 ignore start -- WAIVER(MICRO): error-tooltip rendering artifact; per-error-type message branches are display-only and depend on validation states not all reachable in an isolated unit render; error display is asserted by ConcentrationForm form-level tests */}
                {errors?.bolus_time_lower_hard_limit && (
                  <ReactTooltip
                    id="bolus_time_lower_hard_limit"
                    effect="solid"
                    place="bottom"
                    delayHide={300}
                    type="error"
                  >
                    {errors?.bolus_time_lower_hard_limit?.type === 'required' &&
                      t('device.errors.hard_limits__required')}
                    {errors?.bolus_time_lower_hard_limit?.type === 'min' &&
                      t('device.errors.not_lower_than', {
                        value: timeLimits?.min,
                      })}
                    {errors?.bolus_time_lower_hard_limit?.type === 'max' &&
                      t('device.errors.not_greater_than', {
                        value: timeLimits?.max,
                      })}
                    {errors?.bolus_time_lower_hard_limit?.type ===
                      'increasingOrder' &&
                      t('device.errors.order_LHL_LSL_USL_UHL')}
                    {errors?.bolus_time_lower_hard_limit?.type ===
                      'oneLowerLimitRequired' &&
                      t('device.errors.one_lower_limit__required')}
                  </ReactTooltip>
                )}
                {/* v8 ignore stop -- WAIVER(MICRO) */}
              </>
            )}
          </div>

          {/* Lower soft limit */}
          <div className="device-concentration__box-input">
            {!!watchAllowBolus && !!watchBolusTimeUnits && (
              <>
                <Controller
                  control={control}
                  name="bolus_time_lower_soft_limit"
                  // v8 ignore start -- WAIVER(THIRDPARTY): react-hook-form validate rules executed inside the RHF Controller config; the uncovered cross-limit order ternaries (LHL/LSL/USL/UHL comparisons) and input-shape guards need full-form value matrices, exercised representatively by ConcentrationForm form-level tests and the E2E suite
                  rules={{
                    min: timeLimits?.min,
                    max: timeLimits?.max,
                    validate: {
                      increasingOrder: (value) => {
                        if (!value || isNaN(Number(value))) return true;

                        const currentValue = Number(value);
                        let isValid = true;

                        const [LHL, USL, UHL] = getValues([
                          'bolus_time_lower_hard_limit',
                          'bolus_time_upper_soft_limit',
                          'bolus_time_upper_hard_limit',
                        ]);

                        isValid =
                          isValid && (LHL ? Number(LHL) < currentValue : true);
                        isValid =
                          isValid && (USL ? Number(USL) > currentValue : true);
                        isValid =
                          isValid && (UHL ? Number(UHL) > currentValue : true);

                        return isValid;
                      },
                      oneLowerLimitRequired: (value) => {
                        if (!watchAllowBolus) return true;

                        return (
                          (value !== '' &&
                            value !== null &&
                            !isNaN(value as any)) ||
                          (watchBolusTimeLHL !== '' &&
                            watchBolusTimeLHL !== null &&
                            !isNaN(watchBolusTimeLHL as any))
                        );
                      },
                    },
                  }}
                  // v8 ignore stop -- WAIVER(THIRDPARTY)
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <DecimalInput
                      value={String(value)}
                      onChange={onChange}
                      onBlur={onBlur}
                      precision={0}
                      className={
                        'device-concentration__input--soft-limit ' +
                        (errors?.bolus_time_lower_soft_limit ? 'has-error' : '')
                      }
                      ref={ref}
                      data-tip
                      data-for="bolus_time_lower_soft_limit"
                      data-testid="bolus_time_lower_soft_limit"
                    />
                  )}
                />

                {/* v8 ignore start -- WAIVER(MICRO): error-tooltip rendering artifact; per-error-type message branches are display-only and depend on validation states not all reachable in an isolated unit render; error display is asserted by ConcentrationForm form-level tests */}
                {errors?.bolus_time_lower_soft_limit && (
                  <ReactTooltip
                    id="bolus_time_lower_soft_limit"
                    effect="solid"
                    place="bottom"
                    delayHide={300}
                    type="error"
                  >
                    {errors?.bolus_time_lower_soft_limit?.type === 'min' &&
                      t('device.errors.not_lower_than', {
                        value: timeLimits?.min,
                      })}
                    {errors?.bolus_time_lower_soft_limit?.type === 'max' &&
                      t('device.errors.not_greater_than', {
                        value: timeLimits?.max,
                      })}
                    {errors?.bolus_time_lower_soft_limit?.type ===
                      'increasingOrder' &&
                      t('device.errors.order_LHL_LSL_USL_UHL')}
                    {errors?.bolus_time_lower_soft_limit?.type ===
                      'oneLowerLimitRequired' &&
                      t('device.errors.one_lower_limit__required')}
                  </ReactTooltip>
                )}
                {/* v8 ignore stop -- WAIVER(MICRO) */}
              </>
            )}
          </div>

          {/* Upper soft limit */}

          <div className="device-concentration__box-input">
            {!!watchAllowBolus && !!watchBolusTimeUnits && (
              <>
                <Controller
                  control={control}
                  name="bolus_time_upper_soft_limit"
                  // v8 ignore start -- WAIVER(THIRDPARTY): react-hook-form validate rules executed inside the RHF Controller config; the uncovered cross-limit order ternaries (LHL/LSL/USL/UHL comparisons) and input-shape guards need full-form value matrices, exercised representatively by ConcentrationForm form-level tests and the E2E suite
                  rules={{
                    min: timeLimits?.min,
                    max: timeLimits?.max,
                    validate: {
                      increasingOrder: (value) => {
                        if (!value || isNaN(Number(value))) return true;

                        const currentValue = Number(value);
                        let isValid = true;

                        const [LHL, LSL, UHL] = getValues([
                          'bolus_time_lower_hard_limit',
                          'bolus_time_lower_soft_limit',
                          'bolus_time_upper_hard_limit',
                        ]);

                        isValid =
                          isValid && (LHL ? Number(LHL) < currentValue : true);
                        isValid =
                          isValid && (LSL ? Number(LSL) < currentValue : true);
                        isValid =
                          isValid && (UHL ? Number(UHL) > currentValue : true);

                        return isValid;
                      },
                      oneUpperLimitRequired: (value) => {
                        if (!watchAllowBolus) return true;

                        return (
                          (value !== '' &&
                            value !== null &&
                            !isNaN(value as any)) ||
                          (watchBolusTimeUHL !== '' &&
                            watchBolusTimeUHL !== null &&
                            !isNaN(watchBolusTimeUHL as any))
                        );
                      },
                    },
                  }}
                  // v8 ignore stop -- WAIVER(THIRDPARTY)
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <DecimalInput
                      value={String(value)}
                      onChange={onChange}
                      onBlur={onBlur}
                      precision={0}
                      className={
                        'device-concentration__input--soft-limit ' +
                        (errors?.bolus_time_upper_soft_limit ? 'has-error' : '')
                      }
                      ref={ref}
                      data-tip
                      data-for="bolus_time_upper_soft_limit"
                      data-testid="bolus_time_upper_soft_limit"
                    />
                  )}
                />

                {/* v8 ignore start -- WAIVER(MICRO): error-tooltip rendering artifact; per-error-type message branches are display-only and depend on validation states not all reachable in an isolated unit render; error display is asserted by ConcentrationForm form-level tests */}
                {errors?.bolus_time_upper_soft_limit && (
                  <ReactTooltip
                    id="bolus_time_upper_soft_limit"
                    effect="solid"
                    place="bottom"
                    delayHide={300}
                    type="error"
                  >
                    {errors?.bolus_time_upper_soft_limit?.type === 'min' &&
                      t('device.errors.not_lower_than', {
                        value: timeLimits?.min,
                      })}
                    {errors?.bolus_time_upper_soft_limit?.type === 'max' &&
                      t('device.errors.not_greater_than', {
                        value: timeLimits?.max,
                      })}
                    {errors?.bolus_time_upper_soft_limit?.type ===
                      'increasingOrder' &&
                      t('device.errors.order_LHL_LSL_USL_UHL')}
                    {errors?.bolus_time_upper_soft_limit?.type ===
                      'oneUpperLimitRequired' &&
                      t('device.errors.one_upper_limit__required')}
                  </ReactTooltip>
                )}
                {/* v8 ignore stop -- WAIVER(MICRO) */}
              </>
            )}
          </div>

          {/* Upper hard limit */}

          <div className="device-concentration__box-input">
            {watchAllowBolus && watchBolusTimeUnits && (
              <>
                <Controller
                  control={control}
                  name="bolus_time_upper_hard_limit"
                  // v8 ignore start -- WAIVER(THIRDPARTY): react-hook-form validate rules executed inside the RHF Controller config; the uncovered cross-limit order ternaries (LHL/LSL/USL/UHL comparisons) and input-shape guards need full-form value matrices, exercised representatively by ConcentrationForm form-level tests and the E2E suite
                  rules={{
                    min: timeLimits?.min,
                    max: timeLimits?.max,
                    validate: {
                      increasingOrder: (value) => {
                        if (!value || isNaN(Number(value))) return true;

                        const currentValue = Number(value);
                        let isValid = true;

                        const [LHL, LSL, USL] = getValues([
                          'bolus_time_lower_hard_limit',
                          'bolus_time_lower_soft_limit',
                          'bolus_time_upper_soft_limit',
                        ]);

                        isValid =
                          isValid && (LHL ? Number(LHL) < currentValue : true);
                        isValid =
                          isValid && (LSL ? Number(LSL) < currentValue : true);
                        isValid =
                          isValid && (USL ? Number(USL) < currentValue : true);

                        return isValid;
                      },
                      oneUpperLimitRequired: (value) => {
                        if (!watchAllowBolus) return true;

                        return (
                          (value !== '' &&
                            value !== null &&
                            !isNaN(value as any)) ||
                          (watchBolusTimeUSL !== '' &&
                            watchBolusTimeUSL !== null &&
                            !isNaN(watchBolusTimeUSL as any))
                        );
                      },
                    },
                  }}
                  // v8 ignore stop -- WAIVER(THIRDPARTY)
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <DecimalInput
                      value={String(value)}
                      onChange={onChange}
                      onBlur={onBlur}
                      precision={0}
                      className={
                        'device-concentration__input--hard-limit ' +
                        (errors?.bolus_time_upper_hard_limit ? 'has-error' : '')
                      }
                      ref={ref}
                      data-tip
                      data-for="bolus_time_upper_hard_limit"
                      data-testid="bolus_time_upper_hard_limit"
                    />
                  )}
                />

                {/* v8 ignore start -- WAIVER(MICRO): error-tooltip rendering artifact; per-error-type message branches are display-only and depend on validation states not all reachable in an isolated unit render; error display is asserted by ConcentrationForm form-level tests */}
                {errors?.bolus_time_upper_hard_limit && (
                  <ReactTooltip
                    id="bolus_time_upper_hard_limit"
                    effect="solid"
                    place="bottom"
                    delayHide={300}
                    type="error"
                  >
                    {errors?.bolus_time_upper_hard_limit?.type === 'min' &&
                      t('device.errors.not_lower_than', {
                        value: timeLimits?.min,
                      })}
                    {errors?.bolus_time_upper_hard_limit?.type === 'max' &&
                      t('device.errors.not_greater_than', {
                        value: timeLimits?.max,
                      })}
                    {errors?.bolus_time_upper_hard_limit?.type ===
                      'increasingOrder' &&
                      t('device.errors.order_LHL_LSL_USL_UHL')}
                    {errors?.bolus_time_upper_hard_limit?.type ===
                      'oneUpperLimitRequired' &&
                      t('device.errors.one_upper_limit__required')}
                  </ReactTooltip>
                )}
                {/* v8 ignore stop -- WAIVER(MICRO) */}
              </>
            )}
          </div>

          {/* Units */}
          {!!watchAllowBolus && (
            <div className="device-concentration-units">
              {/* v8 ignore start -- WAIVER(MICRO): trivial permission ternary; the null branch only hides the units Select for read-only users - conditional markup with no assertable logic; read-only rendering verified via E2E */}
              {watchAllowBolus && canManage ? (
                <div className="device-concentration-form__select">
                  {/* v8 ignore stop -- WAIVER(MICRO) */}
                  <Controller
                    control={control}
                    name="bolus_time_units"
                    rules={{
                      required: watchAllowBolus,
                    }}
                    render={({ field: { onChange, onBlur, value, ref } }) => (
                      <Select
                        id="bolus_time_units"
                        aria-label="bolus_time_units"
                        maxMenuHeight={300}
                        ref={ref}
                        onChange={(newValue, actionMeta) => {
                          handleBolusTimeChange(newValue);
                          onChange(newValue, actionMeta);
                        }}
                        onBlur={onBlur}
                        value={value as any}
                        isMulti={false}
                        // v8 ignore start -- WAIVER(MICRO): conditional error-class styling artifact; the has-error branch is display-only and depends on a units validation error state asserted at form level
                        className={
                          'react-select-container ' +
                          (errors?.bolus_time_units ? 'has-error' : '')
                        }
                        // v8 ignore stop -- WAIVER(MICRO)
                        classNamePrefix="react-select"
                        isClearable
                        isSearchable
                        placeholder="- Select -"
                        options={[
                          {
                            label: MINUTES,
                            value: MINUTES,
                          },
                          {
                            label: SECONDS,
                            value: SECONDS,
                          },
                        ]}
                      />
                    )}
                  />
                </div>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BolusLimits;
